export type CallsLiveDeviceScenario =
  | 'foreground'
  | 'background'
  | 'locked-screen'
  | 'silent-mode'
  | 'answer'
  | 'decline'
  | 'missed-call'
  | 'post-call-exit';

export type CallsLiveDeviceEvidenceOutcome =
  | 'pass'
  | 'fail'
  | 'unknown';

export type CallsLiveDeviceEvidenceSource =
  | 'manual-device-test'
  | 'android-runtime'
  | 'command-center'
  | 'governor-verification';

export interface CallsLiveDeviceEvidenceInput {
  readonly scenario: CallsLiveDeviceScenario;
  readonly outcome: CallsLiveDeviceEvidenceOutcome;
  readonly source: CallsLiveDeviceEvidenceSource;
  readonly deviceId?: string;
  readonly callId?: string;
  readonly operatorId?: string;
  readonly detail?: string;
  readonly capturedAt?: string;
}

export interface CallsLiveDeviceEvidenceRecord {
  readonly id: string;
  readonly scenario: CallsLiveDeviceScenario;
  readonly outcome: CallsLiveDeviceEvidenceOutcome;
  readonly source: CallsLiveDeviceEvidenceSource;
  readonly deviceId: string | null;
  readonly callId: string | null;
  readonly operatorId: string | null;
  readonly detail: string | null;
  readonly capturedAt: string;
}

export interface CallsLiveDeviceEvidenceSummary {
  readonly total: number;
  readonly passed: number;
  readonly failed: number;
  readonly unknown: number;
  readonly latestEvidence: CallsLiveDeviceEvidenceRecord | null;
  readonly productionEvidenceComplete: boolean;
}

const MAX_LIVE_DEVICE_EVIDENCE_RECORDS = 250;

const liveDeviceEvidenceRecords: CallsLiveDeviceEvidenceRecord[] = [];

function createEvidenceId(): string {
  const randomPart =
    typeof globalThis.crypto?.randomUUID === 'function'
      ? globalThis.crypto.randomUUID()
      : Math.random().toString(36).slice(2);

  return `calls-live-${Date.now()}-${randomPart}`;
}

function normalizeOptionalValue(value: string | undefined): string | null {
  const normalized = value?.trim();
  return normalized ? normalized.slice(0, 500) : null;
}

function freezeRecord(
  record: CallsLiveDeviceEvidenceRecord,
): CallsLiveDeviceEvidenceRecord {
  return Object.freeze(record);
}

export function recordCallsLiveDeviceEvidence(
  input: CallsLiveDeviceEvidenceInput,
): CallsLiveDeviceEvidenceRecord {
  const record = freezeRecord({
    id: createEvidenceId(),
    scenario: input.scenario,
    outcome: input.outcome,
    source: input.source,
    deviceId: normalizeOptionalValue(input.deviceId),
    callId: normalizeOptionalValue(input.callId),
    operatorId: normalizeOptionalValue(input.operatorId),
    detail: normalizeOptionalValue(input.detail),
    capturedAt: input.capturedAt ?? new Date().toISOString(),
  });

  liveDeviceEvidenceRecords.push(record);

  if (liveDeviceEvidenceRecords.length > MAX_LIVE_DEVICE_EVIDENCE_RECORDS) {
    liveDeviceEvidenceRecords.splice(
      0,
      liveDeviceEvidenceRecords.length - MAX_LIVE_DEVICE_EVIDENCE_RECORDS,
    );
  }

  return record;
}

export function getCallsLiveDeviceEvidence():
  readonly CallsLiveDeviceEvidenceRecord[] {
  return Object.freeze([...liveDeviceEvidenceRecords]);
}

export function getCallsLiveDeviceEvidenceSummary():
  CallsLiveDeviceEvidenceSummary {
  const records = getCallsLiveDeviceEvidence();

  const passed = records.filter(
    (record) => record.outcome === 'pass',
  ).length;

  const failed = records.filter(
    (record) => record.outcome === 'fail',
  ).length;

  const unknown = records.filter(
    (record) => record.outcome === 'unknown',
  ).length;

  const requiredScenarios: readonly CallsLiveDeviceScenario[] = Object.freeze([
    'background',
    'locked-screen',
    'answer',
    'decline',
    'missed-call',
    'post-call-exit',
  ]);

  const productionEvidenceComplete = requiredScenarios.every((scenario) =>
    records.some(
      (record) =>
        record.scenario === scenario &&
        record.outcome === 'pass',
    ),
  );

  return Object.freeze({
    total: records.length,
    passed,
    failed,
    unknown,
    latestEvidence:
      records.length > 0 ? records[records.length - 1] : null,
    productionEvidenceComplete,
  });
}

export function clearCallsLiveDeviceEvidenceForVerification(): void {
  liveDeviceEvidenceRecords.splice(0, liveDeviceEvidenceRecords.length);
}
